export const KDF_ITERATIONS = 600000;
export const LEGACY_KDF_ITERATIONS = 100000;
const PAD_BLOCK = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const DATA_KEY_LENGTH = 32;
function pad(payload) {
    const total = Math.ceil((payload.length + 4) / PAD_BLOCK) * PAD_BLOCK;
    const padded = new Uint8Array(total);
    new DataView(padded.buffer).setUint32(0, payload.length, false);
    padded.set(payload, 4);
    return padded;
}
function unpad(padded) {
    if (padded.length < 4) {
        throw new Error('[cryptography] Malformed padded plaintext.');
    }
    const view = new DataView(padded.buffer, padded.byteOffset, padded.byteLength);
    const length = view.getUint32(0, false);
    if (length > padded.length - 4) {
        throw new Error('[cryptography] Padded length header out of range.');
    }
    return padded.subarray(4, 4 + length);
}
export function getCryptography() {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    function getSalt() {
        return crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    }
    async function deriveKek(password, salt, iterations) {
        const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(password), { name: 'PBKDF2' }, false, [
            'deriveKey',
        ]);
        return await crypto.subtle.deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, keyMaterial, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    }
    async function importDataKey(raw) {
        return await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    }
    async function createDataKey(kek) {
        const raw = crypto.getRandomValues(new Uint8Array(DATA_KEY_LENGTH));
        const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
        const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, kek, raw);
        const dataKey = await importDataKey(raw);
        raw.fill(0);
        return { dataKey, wrapped: { iv, ciphertext } };
    }
    async function unwrapDataKey(kek, wrapped) {
        const raw = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: wrapped.iv }, kek, wrapped.ciphertext));
        const dataKey = await importDataKey(raw);
        raw.fill(0);
        return dataKey;
    }
    async function encrypt(key, value) {
        const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
        const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, pad(encoder.encode(JSON.stringify(value))));
        return { iv, data };
    }
    async function decrypt(key, payload) {
        const padded = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: payload.iv }, key, payload.data));
        return JSON.parse(decoder.decode(unpad(padded)));
    }
    async function legacyHashString(data) {
        const hashBytes = await crypto.subtle.digest('SHA-256', encoder.encode(data));
        return new TextDecoder().decode(hashBytes);
    }
    async function legacyDeriveKey(password, salt) {
        return await deriveKek(password, salt, LEGACY_KDF_ITERATIONS);
    }
    async function legacyDecrypt(key, iv, encryptedData) {
        const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encryptedData);
        return JSON.parse(decoder.decode(decrypted));
    }
    return {
        getSalt,
        deriveKek,
        createDataKey,
        unwrapDataKey,
        encrypt,
        decrypt,
        legacyHashString,
        legacyDeriveKey,
        legacyDecrypt,
    };
}
